import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// CRITICAL: send & receive httpOnly cookies on every request. The backend sets
// `access_token` and `refresh_token` cookies on login/register; the browser
// attaches them automatically when withCredentials is true.
axios.defaults.withCredentials = true;

function formatApiError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = checking, false = not auth'd, object = signed in
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check session via the httpOnly cookie. If the cookie is missing/expired the
  // backend returns 401 and we treat the user as logged out.
  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      // 401 is expected when not signed in; only log unexpected errors.
      if (err?.response?.status !== 401) {
        console.error("[auth] /me failed:", err?.response?.status, err?.message);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      // Cookies are set by the server; the response body carries the user object.
      setUser(res.data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (email, password, name, role) => {
    try {
      const res = await axios.post(`${API}/auth/register`, { email, password, name, role });
      setUser(res.data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      // Server clears the httpOnly cookies.
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.warn("[auth] logout request failed:", err?.message);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
